import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { NavigationEnd, Router } from '@angular/router';
import PerfectScrollbar from 'perfect-scrollbar';
import { AutenticacionService } from '../services/autenticacion.service';
import { TokenService } from '../services/token.service';
import { ChildrenItems, ROUTES, RouteInfo } from './menu-routes';

@Component({
  selector: "app-sidebar-cmp",
  templateUrl: "sidebar.component.html",
  styleUrls: ["sidebar.component.css"],
  animations: [
    trigger('sidebarCollapse', [
      state('collapsed', style({
        height: '0px',
        opacity: 0,
        overflow: 'hidden'
      })),
      state('expanded', style({
        height: '*',
        opacity: 1,
        overflow: 'hidden'
      })),
      transition('collapsed <=> expanded', animate('180ms ease-in-out'))
    ])
  ]
})
export class SidebarComponent implements OnInit, OnDestroy {
  public menuItems: any[];
  ps: any;
  isMobile = false;
  expandedMenus: any = {};
  activeRouteUrl = '';
  private routerSubscription: any;
  private readonly userMenuKey = 'user-menu';
  usuario: string;
  rolPrincipal: string;
  fUltAcceso: string;
  hUltAcceso: string;
  ip: string;

  constructor(
    private tokenService: TokenService,
    private autenticacionService: AutenticacionService,
    private router: Router
  ) { }

  isMobileMenu() {
    return this.isMobile;
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.isMobile = window.innerWidth <= 991;
  }

  ngOnInit() {
    this.isMobile = window.innerWidth <= 991;
    this.menuItems = this.buildVisibleMenu(ROUTES);
    this.updateActiveRoute();
    this.routerSubscription = this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        this.updateActiveRoute();
        setTimeout(() => this.updatePS(), 0);
      }
    });
    if (window.matchMedia(`(min-width: 960px)`).matches && !this.isMac()) {
      const elemSidebar = <HTMLElement>(
        document.querySelector(".sidebar .sidebar-wrapper")
      );
      this.ps = new PerfectScrollbar(elemSidebar);
    }
    this.usuario = this.tokenService.getNombreCompleto();
    this.fUltAcceso = this.tokenService.getFechaUltimoIngreso();
    this.hUltAcceso = this.tokenService.getHoraUltimoIngreso();
    this.ip = this.tokenService.getNroIp();
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  buildVisibleMenu(menuItems: RouteInfo[]) {
    return menuItems
      .filter((menuItem) => this.hasPermission(menuItem))
      .map((menuItem) => {
        const item = Object.assign({}, menuItem);
        if (item.children) {
          item.children = item.children
            .filter((childItem) => this.hasPermission(childItem))
            .map((childItem) => {
              const child = Object.assign({}, childItem);
              if (child.children) {
                child.children = child.children.filter((childItemTwoLevel) =>
                  this.hasPermission(childItemTwoLevel)
                );
              }
              return child;
            });
        }
        return item;
      })
      .filter((menuItem) => menuItem.type !== 'sub' || (menuItem.children && menuItem.children.length > 0));
  }

  hasPermission(menuItem: RouteInfo | ChildrenItems) {
    if (!menuItem.expectedPermission || menuItem.expectedPermission.length === 0) {
      return false;
    }
    const permisos = this.tokenService.getPermissions();
    return permisos.some((item) => menuItem.expectedPermission.includes(item));
  }

  toggleMenu(menuItem: RouteInfo | ChildrenItems, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!menuItem.collapse) {
      return;
    }
    this.expandedMenus[menuItem.collapse] = !this.expandedMenus[menuItem.collapse];
    setTimeout(() => this.updatePS(), 0);
  }

  toggleUserMenu(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.expandedMenus[this.userMenuKey] = !this.expandedMenus[this.userMenuKey];
    setTimeout(() => this.updatePS(), 0);
  }

  isUserMenuExpanded() {
    return this.expandedMenus[this.userMenuKey];
  }

  isExpanded(menuItem: RouteInfo | ChildrenItems) {
    return menuItem.collapse && this.expandedMenus[menuItem.collapse];
  }

  getCollapseState(menuItem: RouteInfo | ChildrenItems) {
    return this.isExpanded(menuItem) ? 'expanded' : 'collapsed';
  }

  getUserCollapseState() {
    return this.isUserMenuExpanded() ? 'expanded' : 'collapsed';
  }

  updateActiveRoute() {
    this.activeRouteUrl = this.normalizeRoute(this.router.url);
    this.expandActiveMenu();
  }

  expandActiveMenu() {
    this.menuItems.forEach((menuItem) => {
      this.expandActiveBranch(menuItem);
    });
  }

  expandActiveBranch(menuItem: RouteInfo | ChildrenItems, parentPath: string = '') {
    if (this.isMenuActive(menuItem, parentPath) && menuItem.collapse) {
      this.expandedMenus[menuItem.collapse] = true;
    }
    if (menuItem.children) {
      const itemPath = this.buildMenuPath(parentPath, menuItem.path);
      menuItem.children.forEach((childItem) => {
        this.expandActiveBranch(childItem, itemPath);
      });
    }
  }

  isMenuActive(menuItem: RouteInfo | ChildrenItems, parentPath: string = '') {
    const itemPath = this.buildMenuPath(parentPath, menuItem.path);
    if (this.isRouteActive(itemPath, this.activeRouteUrl)) {
      return true;
    }
    if (menuItem.children) {
      return menuItem.children.some((childItem) => this.isMenuActive(childItem, itemPath));
    }
    return false;
  }

  buildMenuPath(parentPath: string, path: string) {
    if (!parentPath) {
      return path;
    }
    if (!path) {
      return parentPath;
    }
    const normalizedParent = parentPath.endsWith('/') ? parentPath.slice(0, -1) : parentPath;
    const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
    return normalizedParent + '/' + normalizedPath;
  }

  normalizeRoute(url: string) {
    return url.split('?')[0].split('#')[0];
  }

  isRouteActive(path: string, currentUrl: string) {
    if (!path || path === '/') {
      return currentUrl === path;
    }
    return currentUrl === path || currentUrl.indexOf(path + '/') === 0;
  }

  updatePS(): void {
    if (this.ps && window.matchMedia(`(min-width: 960px)`).matches && !this.isMac()) {
      this.ps.update();
    }
  }
  isMac(): boolean {
    let bool = false;
    if (
      navigator.platform.toUpperCase().indexOf("MAC") >= 0 ||
      navigator.platform.toUpperCase().indexOf("IPAD") >= 0
    ) {
      bool = true;
    }
    return bool;
  }
  salir() {
    this.autenticacionService.logout();
    window.location.reload();
    //  this.router.navigate(['/pages/login']);
  }
}

